import { ArrowDown, ArrowRight, ArrowUp, Minus } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

// A bordered KPI summary tile (uppercase label + mono value, optional leading
// icon + value tint). Shared by the CI trends summary strip + the cryod
// drill-down detail header.
export function KpiTile({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: ReactNode;
  tone?: string;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className={cn("mt-1 font-mono text-xl font-semibold tabular-nums", tone)}>{value}</div>
    </div>
  );
}

// Segmented single-select control (metric/agg/window pickers). Shared by the CI
// trends + cryod overview pages.
export function PillGroup({
  options,
  value,
  onChange,
  disabled,
}: {
  options: ReadonlyArray<{ label: string; value: string }>;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted/50 p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs font-medium transition-all",
            value === option.value
              ? "bg-background text-foreground shadow-sm ring-1 ring-border"
              : "text-muted-foreground hover:text-foreground",
            disabled && "opacity-40 cursor-not-allowed",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// Percent-change indicator. Lower metric is better, so a negative delta is an
// improvement (green); near-zero is neutral. Shared by the CI trends + cryod
// overview pages.
export function DeltaCell({ delta }: { delta: number | null }) {
  if (delta == null) {
    return <Minus className="h-3.5 w-3.5 text-muted-foreground/50" />;
  }
  const absDelta = Math.abs(delta);
  if (absDelta < 1) {
    return (
      <span className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground">
        <ArrowRight className="h-3 w-3" />
        {absDelta.toFixed(1)}%
      </span>
    );
  }
  const isImprovement = delta < 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 font-mono text-xs font-medium",
        isImprovement
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-rose-600 dark:text-rose-400",
      )}
    >
      {isImprovement ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
      {absDelta.toFixed(1)}%
    </span>
  );
}
